import axios from 'axios';
import * as R from 'remeda';
import { RootState } from './store';
import constants from '../constants';
import * as service from '../services/customerService';
import { PayloadAction, createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { CustomerMetadata, CustomerDetails, JobTitle, LoadingState, TeamMember, ChildPracticeMetadata, Customer } from '../types';

export interface CustomerState extends CustomerDetails {
  currentCustomerNumber: string;
  teamMembers: TeamMember[];
  jobTitles: JobTitle[];
  childPractices: ChildPracticeMetadata[];
  childrenById: Record<string, ChildPracticeMetadata> | null;
  customerMetadata: CustomerMetadata | null;
  balance: number;
  error: string | null;
  loadingState: LoadingState;
}

const initialState: CustomerState = {
  city: '',
  corporate_parent: false,
  customer_number: '',
  customerService: [],
  doctor_name: '',
  email: '',
  groups: [],
  is_taxable: true,
  name: '',
  phone: '',
  fax: '',
  state: '',
  street: '',
  street2: '',
  zip: '',
  currentCustomerNumber: '',
  teamMembers: [],
  childPractices: [],
  childrenById: null,
  customerMetadata: null,
  jobTitles: [],
  error: null,
  balance: 0,
  is_bill_to_parent: false,
  loadingState: LoadingState.NotStarted,
};

export const getSingleChildPractice = createAsyncThunk(
  'setSingleChildPractice',
  async ({ customerNumber, childCustomerNumber }: { customerNumber: string; childCustomerNumber: string }) => {
    const response = await axios.get(
      `${constants.ONTRAQ_API_URL}/api2/${customerNumber}/customer/corp-children/${childCustomerNumber}`
    );
    return response.data as ChildPracticeMetadata | ChildPracticeMetadata[] | null;
  }
);

export const getCustomerGroups = createAsyncThunk('setCustomerGroups', service.getGroups)
export const getCustomerService = createAsyncThunk('setCustomerService', service.getCustomerService)
export const getCustomerAddress = createAsyncThunk('setCustomerAddress', service.getCustomerAddress)
export const getTeamMembers = createAsyncThunk('setTeamMembers', service.getTeamMembers)
export const getCustomerMetadata = createAsyncThunk('setCustomerMetadata', service.getCustomerMetadata)
export const getJobTitles = createAsyncThunk('setJobTitles', service.getJobTitles)
export const getBalance = createAsyncThunk('setBalance', service.getBalance)
export const getChildPractices = createAsyncThunk('setChildPractices', service.getChildPractices)

export const getCustomerData = createAsyncThunk(
  'getCustomerData',
  async (customer: Customer, { dispatch }) => {
    const { customer_number, corporate_parent } = customer

    const services = [
      dispatch(getJobTitles()),
      dispatch(getBalance(customer_number)),
      dispatch(getTeamMembers(customer_number)),
      dispatch(getCustomerGroups(customer_number)),
      dispatch(getCustomerService(customer_number)),
      dispatch(getCustomerAddress(customer_number)),
      dispatch(getCustomerMetadata(customer_number))
    ]

    if (corporate_parent) {
      services.push(
        dispatch(getChildPractices(customer_number))
      )
    }

    await Promise.all(services)
  }
)

export const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setCustomer: (
      state,
      action: PayloadAction<Customer>
    ) => {
      return {
        ...state,
        ...R.pick(action.payload, [
          'corporate_parent',
          'customer_number',
          'doctor_name',
          'is_taxable',
          'name',
        ])
      };
    },
    setCurrentCustomerNumber: (state, action: PayloadAction<string>) => {
      state.currentCustomerNumber = action.payload;
    },
    clearCustomer: () => {
      return initialState;
    },
    clearChildPractices: (state) => {
      state.childPractices = initialState.childPractices
    },
  },
  extraReducers(builder) {
    builder.addCase(getCustomerService.fulfilled, (state, action) => {
      state.customerService = action.payload
    })
    builder.addCase(getCustomerAddress.fulfilled, (state, action) => {
      const address = action.payload

      state.street = address?.street ?? ''
      state.street2 = address?.street2 ?? ''
      state.city = address?.city ?? ''
      state.state = address?.state ?? ''
      state.zip = address?.zip ?? ''
      state.email = address?.email ?? ''
      state.phone = address?.phone ?? ''
      state.fax = address?.fax ?? ''
    })
    builder.addCase(getTeamMembers.fulfilled, (state, action) => {
      if (!state) {
        return;
      }
      state.teamMembers = action.payload ?? []
    });
    builder.addCase(getChildPractices.fulfilled, (state, action) => {
      if (!state) { return }

      state.childPractices = action.payload

      state.childrenById = Object.fromEntries(
        action.payload.map(child => [child.customer_number, child])
      )
    });
    builder.addCase(getChildPractices.pending, (state) => {
      state.error = null;
    });
    builder.addCase(getChildPractices.rejected, (state) => {
      state.error = 'The practices could not be loaded, try again later.';
    });
    builder.addCase(getSingleChildPractice.fulfilled, (state, action) => {
      const childPractice = Array.isArray(action.payload) ? action.payload[0] : action.payload;
      if (!childPractice) return;

      const existingIndex = state.childPractices.findIndex(
        (practice) => practice.customer_number === childPractice.customer_number
      );

      if (existingIndex >= 0) {
        state.childPractices[existingIndex] = childPractice;
      } else {
        state.childPractices.push(childPractice);
      }

      state.childrenById = {
        ...(state.childrenById ?? {}),
        [childPractice.customer_number]: childPractice,
      };
    });
    builder.addCase(getSingleChildPractice.pending, (state) => {
      state.error = null;
    });
    builder.addCase(getSingleChildPractice.rejected, (state) => {
      state.error = 'The practice could not be loaded, try again later.';
    });
    builder.addCase(getCustomerMetadata.fulfilled, (state, action) => {
      if (!state) {
        return;
      }
      state.customerMetadata = action.payload;
    });
    builder.addCase(getCustomerMetadata.pending, (state) => {
      state.error = null;
    });
    builder.addCase(getCustomerMetadata.rejected, (state) => {
      state.error = 'The practice metadata could not be loaded, try again later.';
    });
    builder.addCase(getCustomerGroups.fulfilled, (state, action) => {
      if (!state) return;
      state.groups = action.payload;
    });
    builder.addCase(getJobTitles.fulfilled, (state, action) => {
      if (!state) return
      state.jobTitles = action.payload
    })
    builder.addCase(getBalance.fulfilled, (state, action) => {
      if(!state) return
      state.balance = action.payload
    })

    builder.addMatcher(isPending(getCustomerData), (state) => {
      state.loadingState = LoadingState.Loading
    })

    builder.addMatcher(isRejected(getCustomerData), (state) => {
      state.loadingState = LoadingState.Error
    })

    builder.addMatcher(isFulfilled(getCustomerData), (state, { payload }) => {
      state.loadingState = LoadingState.Done
    })
  }
});

export const { setCustomer, setCurrentCustomerNumber, clearCustomer, clearChildPractices } = customerSlice.actions;

export const selectGroups = (state: RootState) => state.customer.groups;

export const selectChildPractices = (state: RootState) => state.customer.childPractices;
export const selectChildrenById = (state: RootState) => state.customer.childrenById;

export const selectShowAutoclaveConfirmationAlertForCorp = (state: RootState) =>
  state.customer.childPractices.some(
    (p) => p.inactive === false && (p.autoclave_outstanding ?? 0) > 0
  );

export const selectCustomerNumber = (state: RootState) => state.customer.customer_number;

export const selectCustomerMetadata = (state: RootState) => state.customer.customerMetadata;

export const selectCurrentCustomerNumber = (state: RootState) => state.customer.currentCustomerNumber;

export const selectHasDE = (state: RootState) =>
  state.customer.customerService.some((service) => service.serviceType === 'DE' && service.isActive);

export const selectTeamMembers = (state: RootState) => state.customer.teamMembers;

export const selectJobTitles = (state: RootState) => state.customer.jobTitles;

export const selectCustomerError = (state: RootState) => state.customer.error;

export const selectIsCorporateParent = (state: RootState) => state.customer.corporate_parent;

export const selectLoadingState = (state: RootState) => state.customer.loadingState

export const selectCustomer = (state: RootState) => state.customer;

export const selectBalance = (state: RootState) => state.customer.balance;

export default customerSlice.reducer;
